import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey, $getRoot } from "lexical";
import { useCallback, useEffect, useState } from "react";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { serviceURL } from "../../../../utils/service-url";
import { LinkForm } from "../components/link-form/link-form";
import { useLexicalContext } from "../lexical-context";
import { useSelectable } from "../utils/use-selectable";
import { $createImageNodeOfClass, $isImageNode, ImageNode } from "./image-node";
import { MuiImageComponent } from "./mui-image-component";
import { convertImageElement, isImageElement } from "./utils";

import type { UUID } from "../../../../utils/uuid";
import type { Link } from "../components/link-form/types";
import type { SerializedImageNode } from "./image-node";
import type { DOMConversionMap, ElementFormatType, LexicalNode, NodeKey } from "lexical";
import type { FC } from "react";

type ResizePermissionType = { allowed: true } | { allowed: false; reason: string };

const ImageComponent: FC<{
  nodeKey: NodeKey;
  src: string;
  proportionOfMaxWidth: number;
  aspect: number;
  format?: ElementFormatType;
  href?: string;
  resizingAllowed: (channel: string) => ResizePermissionType;
}> = ({
  nodeKey,
  format,
  proportionOfMaxWidth,
  aspect,
  src,
  href,
  resizingAllowed: resizingAllowedFloatingToolbar,
}) => {
  useSelectable(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [disabled, setDisabled] = useState(!editor.isEditable());
  const { handleClose, open } = useDialog();
  const { filterTags } = useLexicalContext();

  useEffect(() => editor.registerEditableListener((x) => setDisabled(!x)), [editor]);

  const handleLinkUpdate = useCallback(
    ({ url }: Link) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) node.setHref(url);

        handleClose();
      });
    },
    [editor, nodeKey, handleClose],
  );

  const handleLinkRemove = useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) node.setHref();

      handleClose();
    });
  }, [editor, nodeKey, handleClose]);

  return (
    <>
      <MuiImageComponent
        nodeKey={nodeKey}
        src={src}
        aspect={aspect}
        proportionOfMaxWidth={proportionOfMaxWidth}
        resizingAllowed={resizingAllowedFloatingToolbar}
        format={format}
      />
      {open && (
        <LinkForm
          onRemove={handleLinkRemove}
          onCancel={handleClose}
          onConfirm={handleLinkUpdate}
          disabled={disabled}
          filterTags={filterTags}
          data={
            href
              ? { mode: "edit", url: href, type: "image" }
              : { mode: "add", url: undefined, type: "image" }
          }
        />
      )}
    </>
  );
};

export class ImageNodeMui extends ImageNode {
  __currentlyResizing: boolean = false;
  decorate(): React.JSX.Element {
    return (
      <ImageComponent
        nodeKey={this.__key}
        src={this.__url}
        proportionOfMaxWidth={this.__proportionOfMaxWidth}
        aspect={this.__aspect}
        format={this.__format}
        href={this.__href}
        resizingAllowed={this.resizingAllowed}
      />
    );
  }
  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    return super.importJSON(serializedNode);
  }
  exportJSON(): SerializedImageNode {
    return super.exportJSON();
  }
  static clone(node: ImageNode): ImageNode {
    return super.clone(node);
  }
  static getType(): string {
    return super.getType();
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (imageNode: HTMLElement) => {
        if (!isImageElement(imageNode)) return null;

        return {
          conversion: (n) => convertImageElement(n, ImageNodeMui),
          priority: 0,
        };
      },
    };
  }

  resizingAllowed = (channel: string): ResizePermissionType => {
    if (channel !== "slack") return { allowed: true };
    if (!this.__url.startsWith(serviceURL("images")))
      return {
        allowed: false,
        reason: "This is a link to an external image and cannot be resized in Slack",
      };
    if (this.__url.endsWith(".gif"))
      return { allowed: false, reason: "This file type cannot be resized in Slack" };

    return { allowed: true };
  };

  setIsCurrentlyResizing(isCurrentlyResizing: boolean): void {
    const writable = this.getWritable();
    writable.__currentlyResizing = isCurrentlyResizing;
  }

  getIsCurrentlyResizing(): boolean {
    return this.__currentlyResizing;
  }
}

export function $createImageNode(
  headless: boolean,
  url: string,
  proportionOfMaxWidth: number,
  aspect: number,
  href?: string,
  sourceAssetId?: UUID,
): ImageNode {
  return $createImageNodeOfClass(
    headless ? ImageNode : ImageNodeMui,
    url,
    proportionOfMaxWidth,
    aspect,
    href,
    sourceAssetId,
  );
}

export function $removePlaceholderImage(): void {
  const placeholderImageNode = $getRoot()
    .getChildren()
    .find(
      (x) =>
        $isImageNode(x) && x.__url === "https://images.changeengine.com/Placeholder-image-400.png",
    );
  placeholderImageNode?.remove();
}

export function $isImageNodeMui(node: LexicalNode | null | undefined): node is ImageNodeMui {
  return node instanceof ImageNodeMui;
}
